/**
 * The client side of the protocol: one socket, a set of channel subscriptions, and the
 * bookkeeping that keeps them honest across a reconnect.
 *
 * It lives here rather than in `client/` and knows nothing about React or `WebSocket`,
 * because the behaviour it implements is part of the wire contract and needs testing.
 * The 2023 client reconnected its socket perfectly well and then never re-subscribed to
 * anything, so a board that had been live simply stopped updating — no error, no
 * indication, just a position frozen at whatever it was when the network hiccuped. That
 * is the single most important line in this file:
 *
 *     on reconnect, every channel is subscribed again.
 *
 * The second is gap detection. Every event carries a sequence number that is contiguous
 * per channel, so a client that misses one knows it missed one, and resubscribes rather
 * than applying an event to a state that never received its predecessor.
 */

import type { ClientMessage, ServerMessage } from './protocol.ts';
import { identifyWith, type Identity } from './identity.ts';

/** A socket, reduced to what this file needs. Tests supply a fake. */
export interface Transport {
  send(data: string): void;
  close(): void;
}

export interface TransportHandlers {
  onOpen(): void;
  onMessage(data: string): void;
  onClose(): void;
}

export type Connect = (handlers: TransportHandlers) => Transport;

/**
 * `stale` is the interesting one: connected but not yet resynced, or disconnected with
 * the last known state still on screen. A silent stale board is worse than a visible
 * reconnect, so this is surfaced rather than hidden.
 */
export type ConnectionStatus = 'connecting' | 'authenticating' | 'ready' | 'offline';

export interface ClientEvents {
  onStatus?(status: ConnectionStatus): void;
  onWelcome?(user: { userId: string; username: string }): void;
  onError?(error: { code: string; message: string }): void;
  /** Server time minus local time, in ms. Clocks render against this. */
  onClockOffset?(offsetMs: number): void;
}

export interface ClientOptions extends ClientEvents {
  connect: Connect;
  identity: Identity;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => number;
  cancel?: (handle: number) => void;
}

/** Reconnection backoff, in ms. Capped, and the first retry is quick. */
const BACKOFF = [250, 500, 1000, 2000, 5000, 10_000];

/** How often to ping, and how long to wait for the pong before giving up on the socket. */
export const HEARTBEAT_MS = 25_000;
export const HEARTBEAT_TIMEOUT_MS = 10_000;

type Listener = (state: unknown, stale: boolean) => void;

interface Subscription {
  readonly channel: string;
  readonly reduce: (state: never, event: never) => unknown;
  readonly listeners: Set<Listener>;
  state: unknown;
  seq: number;
  /** True between losing sync and receiving the next reset. */
  stale: boolean;
}

export class ProtocolClient {
  private transport: Transport | undefined;
  private state: ConnectionStatus = 'offline';
  private attempt = 0;
  private closed = false;

  private readonly subscriptions = new Map<string, Subscription>();
  /** Packets sent before the handshake finished, replayed once it has. */
  private readonly pending: ClientMessage[] = [];

  private heartbeat: number | undefined;
  private heartbeatDeadline: number | undefined;
  private reconnectTimer: number | undefined;

  private user: { userId: string; username: string } | undefined;

  private readonly now: () => number;
  private readonly schedule: (fn: () => void, ms: number) => number;
  private readonly cancel: (handle: number) => void;

  constructor(private readonly options: ClientOptions) {
    this.now = options.now ?? (() => Date.now());
    this.schedule = options.schedule ??
      ((fn, ms) => setTimeout(fn, ms) as unknown as number);
    this.cancel = options.cancel ?? ((h) => clearTimeout(h));
  }

  get status(): ConnectionStatus {
    return this.state;
  }

  get userId(): string | undefined {
    return this.user?.userId;
  }

  open(): void {
    if (this.closed || this.transport) return;
    this.setStatus('connecting');

    this.transport = this.options.connect({
      onOpen: () => this.setStatus('authenticating'),
      onMessage: (data) => this.receive(data),
      onClose: () => this.dropped(),
    });
  }

  close(): void {
    this.closed = true;
    this.clearTimers();
    this.transport?.close();
    this.transport = undefined;
    this.setStatus('offline');
  }

  /**
   * Watch a channel. The reducer is the one from `rooms.ts`, so the state a client holds
   * is produced by the same code that produced the server's.
   *
   * Several callers may watch the same channel; the socket subscribes once.
   */
  subscribe<S, E>(
    channel: string,
    reduce: (state: S, event: E) => S,
    listener: (state: S | undefined, stale: boolean) => void,
  ): () => void {
    let sub = this.subscriptions.get(channel);
    if (!sub) {
      sub = {
        channel,
        reduce: reduce as Subscription['reduce'],
        listeners: new Set(),
        state: undefined,
        seq: 0,
        stale: true,
      };
      this.subscriptions.set(channel, sub);
      if (this.state === 'ready') this.send({ t: 'sub', ch: channel });
    }

    sub.listeners.add(listener as Listener);
    listener(sub.state as S | undefined, sub.stale);

    return () => {
      const current = this.subscriptions.get(channel);
      if (!current) return;
      current.listeners.delete(listener as Listener);
      if (current.listeners.size > 0) return;

      this.subscriptions.delete(channel);
      if (this.state === 'ready') this.send({ t: 'unsub', ch: channel });
    };
  }

  /** Send a packet, queueing it if the handshake has not finished. */
  send(message: ClientMessage): void {
    if (this.state !== 'ready' || !this.transport) {
      // Subscriptions are re-sent wholesale on reconnect, so queueing them here as well
      // would double them up.
      if (message.t !== 'sub' && message.t !== 'unsub') this.pending.push(message);
      return;
    }
    this.transport.send(JSON.stringify(message));
  }

  // --- inbound ------------------------------------------------------------

  private receive(data: string): void {
    let message: ServerMessage;
    try {
      message = JSON.parse(data) as ServerMessage;
    } catch {
      return;
    }

    switch (message.t) {
      case 'challenge':
        void this.answer(message.nonce);
        this.options.onClockOffset?.(message.serverTime - this.now());
        return;

      case 'welcome':
        this.user = { userId: message.userId, username: message.username };
        this.options.onClockOffset?.(message.serverTime - this.now());
        this.options.onWelcome?.(this.user);
        this.ready();
        return;

      case 'reset': {
        const sub = this.subscriptions.get(message.ch);
        if (!sub) return;
        sub.state = message.state;
        sub.seq = message.seq;
        sub.stale = false;
        this.publish(sub);
        return;
      }

      case 'ev': {
        const sub = this.subscriptions.get(message.ch);
        if (!sub || sub.state === undefined) return;

        if (message.seq !== sub.seq + 1) {
          // A gap. Whatever we hold is now unreliable, so ask for the whole thing
          // again rather than applying an event to a state missing its predecessor.
          sub.stale = true;
          this.publish(sub);
          this.send({ t: 'sub', ch: sub.channel });
          return;
        }

        sub.seq = message.seq;
        sub.state = (sub.reduce as (s: unknown, e: unknown) => unknown)(
          sub.state,
          message.e,
        );
        this.publish(sub);
        return;
      }

      case 'pong':
        this.options.onClockOffset?.(message.serverTime - this.now());
        // The socket is alive; arm the next beat.
        if (this.state === 'ready') this.startHeartbeat();
        return;

      case 'err':
        this.options.onError?.({ code: message.code, message: message.message });
        return;
    }
  }

  private async answer(nonce: string): Promise<void> {
    const packet = await identifyWith(this.options.identity, nonce);
    // The socket may have died while the signature was being computed.
    if (this.transport && this.state === 'authenticating') {
      this.transport.send(JSON.stringify(packet));
    }
  }

  /**
   * The handshake finished. Re-subscribe everything, then flush anything the caller sent
   * while we were still connecting.
   */
  private ready(): void {
    this.attempt = 0;
    this.setStatus('ready');

    for (const sub of this.subscriptions.values()) {
      // Not conditional on this being a *re*connect: a first connection and a recovery
      // take the identical path, so the recovery path cannot rot from disuse.
      this.transport?.send(JSON.stringify({ t: 'sub', ch: sub.channel }));
    }

    const queued = this.pending.splice(0, this.pending.length);
    for (const message of queued) this.transport?.send(JSON.stringify(message));

    this.startHeartbeat();
  }

  private publish(sub: Subscription): void {
    for (const listener of sub.listeners) listener(sub.state, sub.stale);
  }

  // --- connection lifecycle ----------------------------------------------

  private dropped(): void {
    this.transport = undefined;
    this.clearTimers();
    if (this.closed) return;

    // Keep the last known state on screen, but mark it stale so the interface can say
    // so. A frozen board that looks live is the worst of the options.
    for (const sub of this.subscriptions.values()) {
      sub.stale = true;
      this.publish(sub);
    }

    this.setStatus('connecting');
    const delay = BACKOFF[Math.min(this.attempt, BACKOFF.length - 1)];
    this.attempt++;
    this.reconnectTimer = this.schedule(() => {
      this.reconnectTimer = undefined;
      this.open();
    }, delay);
  }

  private startHeartbeat(): void {
    this.clearHeartbeat();
    this.heartbeat = this.schedule(() => {
      this.heartbeat = undefined;
      if (this.state !== 'ready') return;

      this.send({ t: 'ping', at: this.now() });
      this.heartbeatDeadline = this.schedule(() => {
        this.heartbeatDeadline = undefined;
        // TCP can take minutes to notice a dead peer. This notices in ten seconds.
        this.transport?.close();
        this.dropped();
      }, HEARTBEAT_TIMEOUT_MS);
    }, HEARTBEAT_MS);
  }

  private clearHeartbeat(): void {
    for (const handle of [this.heartbeat, this.heartbeatDeadline]) {
      if (handle !== undefined) this.cancel(handle);
    }
    this.heartbeat = undefined;
    this.heartbeatDeadline = undefined;
  }

  private clearTimers(): void {
    this.clearHeartbeat();
    if (this.reconnectTimer !== undefined) {
      this.cancel(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.state === status) return;
    this.state = status;
    this.options.onStatus?.(status);
  }
}
